import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, NoDataMessage } from './RecordSection.styled';
import { 
  VitalCard,
  MealCard,
  HydrationCard,
  TrainingBathCard,
} from './components/RecordCard';
import {
  TemperatureIcon,
  BloodPressureIcon,
  MealIcon,
  HydrationIcon,
  ExerciseIcon,
  BathIcon,
  SpO2Icon,
} from '../../../../components/Icons';
import { formatMealIntake } from '../../../../utils/recordFormat';

interface Vitals {
  temp_c?: number;
  sbp?: number;
  dbp?: number;
  pulse?: number;
  spo2?: number;
}

interface Meal {
  staple_food?: number;  // percentage 0-100
  side_dish?: number;    // percentage 0-100
  soup_food?: number;    // percentage 0-100
  is_not_provided?: boolean;
}

interface Hydration {
  amount_ml: number;
}

interface Bathing {
  status: number;         // 0: 未実施, 1: 実施
  is_recorded: boolean;
}

interface Training {
  status: number;         // 0: 未実施, 1: 実施
}

interface RecordSectionProps {
  patientId?: number | null; // 患者ID（健康グラフへの遷移時に使用）
  vitals?: Vitals | null;
  meal?: Meal | null;
  hydration?: Hydration | null;
  bathing?: Bathing | null;
  training?: Training | null;
  selectedDate?: Date; // 選択中の日付（健康グラフへの遷移時に使用）
}

const RecordSectionComponent: React.FC<RecordSectionProps> = ({
  patientId,
  vitals,
  meal,
  hydration,
  bathing,
  training,
  selectedDate,
}) => {
  const navigate = useNavigate();

  // 健康グラフへの遷移パスを生成（date と patient_id パラメータを含める）
  const getHealthGraphPath = (vital: string): string => {
    const params = new URLSearchParams({ vital });
    if (selectedDate) {
      const dateString = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD形式
      params.append('date', dateString);
    }
    if (patientId != null) {
      params.append('patient_id', patientId.toString());
    }
    return `/health-graph?${params.toString()}`;
  };

  // 食事摂取割合を変換 (0-100% → 0-10)
  const convertMealPercentage = (value: number | undefined): number | undefined => {
    if (value == null) return undefined;
    if (value >= 100) return 10;
    return Math.floor(value / 10);
  };

  // 実施状況をフォーマット (0: 未実施, 1: 実施)
  const formatImplementationStatus = (status: number | undefined): string => {
    if (status == null) return 'ー';
    return status === 1 ? '実施' : '未実施';
  };

  if (!vitals) {
    return (
      <NoDataMessage>本日のバイタルデータはまだありません。</NoDataMessage>
    );
  }

  return (
    <Container>
      {/* 体温 */}
      {vitals.temp_c != null && (
        <VitalCard
          icon={<TemperatureIcon />}
          label="体温"
          value={vitals.temp_c.toFixed(1)}
          unit="℃"
          onClick={() => navigate(getHealthGraphPath('temperature'))}
        />
      )}

      {/* 血圧脈拍 */}
      {vitals.sbp != null && vitals.dbp != null && vitals.pulse != null && (
        <VitalCard
          icon={<BloodPressureIcon />}
          label="血圧脈拍"
          value={`${vitals.sbp}/${vitals.dbp}`}
          unit="mmHg"
          supplementValue={vitals.pulse.toString()}
          supplementUnit="bpm"
          onClick={() => navigate(getHealthGraphPath('blood_pressure_pulse'))}
        />
      )}

      {/* SpO2 */}
      {vitals.spo2 != null && (
        <VitalCard
          icon={<SpO2Icon />}
          label="SpO2"
          value={vitals.spo2.toString()}
          unit="%"
          onClick={() => navigate(getHealthGraphPath('spo2'))}
        />
      )}

      {/* 食事 */}
      {meal && !meal.is_not_provided && (meal.staple_food != null || meal.side_dish != null || meal.soup_food != null) && (
        <MealCard
          icon={<MealIcon />}
          label="食事"
          stapleFoodLabel="主食"
          stapleFood={meal.staple_food != null ? `${formatMealIntake(convertMealPercentage(meal.staple_food))}` : undefined}
          sideDishLabel="副食"
          sideDish={meal.side_dish != null ? `${formatMealIntake(convertMealPercentage(meal.side_dish))}` : undefined}
          soupFoodLabel="汁食"
          soupFood={meal.soup_food != null ? `${formatMealIntake(convertMealPercentage(meal.soup_food))}` : undefined}
          onClick={() => navigate(getHealthGraphPath('hydration'))}
        />
      )}

      {/* 水分 */}
      {hydration && (
        <HydrationCard
          icon={<HydrationIcon />}
          label="水分"
          value={hydration.amount_ml}
          unit="ml"
          onClick={() => navigate(getHealthGraphPath('hydration'))}
        />
      )}

      {/* 運動・入浴 */}
      {(training !== null || bathing !== null) && (
        <TrainingBathCard
          // 運動: trainingがnullでない場合のみ表示
          icon1={training !== null ? <ExerciseIcon /> : undefined}
          label1={training !== null ? "運動" : undefined}
          value1={training !== null ? formatImplementationStatus(training?.status) : undefined}
          // 入浴: bathingがnullでない場合のみ表示
          icon2={bathing !== null ? <BathIcon /> : undefined}
          label2={bathing !== null ? "入浴" : undefined}
          value2={bathing !== null ? formatImplementationStatus(bathing?.status) : undefined}
          clickable={false}
        />
      )}
    </Container>
  );
};

// React.memoで最適化: propsが変わらない限り再レンダリングしない
export const RecordSection = React.memo(RecordSectionComponent);

export default RecordSection;
